/**
 * A similar open lead (decision of Nov 3, 2026): a contact may have
 * several leads, when they are different projects. A new lead that looks
 * like an open lead of the same contact (same company and same
 * applications, or created the same day for the same show) is flagged,
 * never blocked: the seller opens the existing one or creates it anyway.
 */

type LeadLike = Readonly<Record<string, unknown>>;

const key = (v: unknown) =>
  typeof v === 'string'
    ? v
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
    : '';

const set = (v: unknown) => (Array.isArray(v) ? [...new Set(v.map(String))].sort().join(',') : '');

/** The local day (YYYY-MM-DD) of a timestamp, in the time zone of this device. */
function localDay(v: unknown): string | null {
  if (typeof v !== 'string' || !v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Open: not deleted, not disqualified. */
export function isOpenLead(lead: object): boolean {
  const l = lead as LeadLike;
  return !l.deleted && !l.deleted_at && l.qualification !== 'disqualified';
}

/**
 * The open leads of the contact that look like the new one. `today` is the
 * device's date (YYYY-MM-DD); the new lead is created today.
 */
export function similarOpenLeads<T extends object>(
  existing: readonly T[],
  next: { company: string | null; applications: readonly string[]; eventId: string | null },
  today: string,
): T[] {
  const company = key(next.company);
  const apps = set(next.applications);
  return existing.filter((lead) => {
    const l = lead as LeadLike;
    if (!isOpenLead(l)) return false;
    const sameProject =
      company !== '' &&
      key(l.company_name) === company &&
      apps !== '' &&
      set(l.applications) === apps;
    const sameShowSameDay =
      localDay(l.created_at) === today && (l.event_id ?? null) === (next.eventId ?? null);
    return sameProject || sameShowSameDay;
  });
}
